// app.domains — request custom domains (web + email) + email activation.
//
// Privileged domain/email ops need the provisioning secret, which only the
// PLATFORM runtime holds. So a project doesn't do the work itself — it drops a
// request under its own IAM-gated prefix on the platform bucket (the
// interchange); the platform runtime fulfills it and writes status back into
// THIS project's own bucket at `paths.domainRecord(host)`, which
// `list()`/`status()` read. The slug is enforced by the IAM path, so the
// platform trusts the key, not the body. (See cognito-email / domain-resources.)
//
// Status reads go through paths.ts DIRECTLY, not a collection: domain records
// are platform bookkeeping (v2: `.s3p/domains/…`), not app data — reading them
// as a `domains` collection pointed at the app database and silently found
// nothing on layout-2 projects.

import {
  GetObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import { interchangeKey } from "./interchange.ts";
import {
  type EmailSenders,
  authSenderRequest,
  authSenderRequestId,
  type DomainAction,
  type DomainKind,
  type DomainRecord,
  type EmailStatus,
  domainRequestBody,
  domainRequestId,
  normalizeHost,
  senderNameRequest,
  senderNameRequestId,
} from "./domain-contract.ts";

// The wire contract (keys + bodies + record shapes) is shared with `s3p
// domains` — see domain-contract.ts. Re-exported so existing importers of
// `@s3p/sdk` keep resolving these names from here.
export type {
  EmailSenders,
  DomainAction,
  DomainDnsRecord,
  DomainKind,
  DomainRecord,
  EmailStatus,
} from "./domain-contract.ts";
import type { Paths } from "./paths.ts";

export interface DomainsModule {
  /** Request a custom-domain attach. The platform creates the ACM cert / SES
   *  identity and writes the DNS records to publish into this project's
   *  `domains` collection — poll `status(host)` for them. */
  add(host: string, kind?: DomainKind): Promise<void>;
  /** Ask the platform to finish the attach (once the DNS records are live). */
  verify(host: string): Promise<void>;
  /** Detach a custom domain. Always allowed (never entitlement-gated). The
   *  platform deletes the route (the edge stops resolving it), disables a
   *  primary's dedicated distribution, drops the SES identity, and removes
   *  this project's status record — `list()` stops returning it. A primary
   *  with attached subdomains is refused until they're removed. */
  remove(host: string): Promise<void>;
  /** Ask the platform to (re-)activate sending Cognito email from this
   *  project's own domain once `{slug}.s3p.dev` is verified. */
  activateEmail(): Promise<void>;
  /** All domains recorded for this project. */
  list(): Promise<DomainRecord[]>;
  /** One domain's current record (records to publish, status, error), or null. */
  status(host: string): Promise<DomainRecord | null>;
  /** Which domains this project may send from, and which one carries auth
   *  mail. Null until the platform has written it (no custom email domain). */
  senders(): Promise<EmailSenders | null>;
  /** Move auth mail (sign-in codes, invites, resets) to `host`, or back to the
   *  built-in domain when omitted. The host must already be attached AND
   *  verified for email — the platform refuses otherwise. */
  useForAuth(host?: string): Promise<void>;
  /** Set the From DISPLAY NAME recipients see (`Acme <noreply@acme.com>`), or
   *  clear it when omitted. Applies to Cognito's sign-in/invite/reset mail and
   *  to `app.email.send()` alike — one name per project. Does NOT move which
   *  domain sends. */
  setSenderName(name?: string): Promise<void>;
  /** The outcome of the last `activateEmail()` — the status record the
   *  platform writes back (verified? developer mode? error?). Null until the
   *  first activation request has been processed. */
  emailStatus(): Promise<EmailStatus | null>;
}

export function createDomains(cfg: {
  getClient: () => Promise<S3Client>;
  slug: string;
  /** The platform bucket — where requests are dropped (`s3p-{acct}-platform`). */
  consoleBucket: string;
  /** THIS project's bucket — where the platform writes status records back. */
  bucket: string;
  paths: Paths;
}): DomainsModule {
  const host = normalizeHost;

  async function drop(requestKey: string, body: Record<string, unknown>): Promise<void> {
    const s3 = await cfg.getClient();
    await s3.send(
      new PutObjectCommand({
        Bucket: cfg.consoleBucket,
        Key: interchangeKey(cfg.slug, requestKey),
        Body: JSON.stringify(body),
        ContentType: "application/json",
      }),
    );
  }

  async function readJson<T>(key: string): Promise<T | null> {
    try {
      const s3 = await cfg.getClient();
      const r = await s3.send(new GetObjectCommand({ Bucket: cfg.bucket, Key: key }));
      const t =
        (await (r.Body as { transformToString(): Promise<string> }).transformToString()) ?? "";
      return t ? (JSON.parse(t) as T) : null;
    } catch {
      return null;
    }
  }
  const readRecord = (key: string) => readJson<DomainRecord>(key);

  return {
    async add(h, kind = "both") {
      await drop(domainRequestId("start", h), domainRequestBody("start", h, kind));
    },
    async verify(h) {
      await drop(domainRequestId("verify", h), domainRequestBody("verify", h));
    },
    async remove(h) {
      await drop(domainRequestId("remove", h), domainRequestBody("remove", h));
    },
    async activateEmail() {
      await drop("activate-email", { kind: "activate-email" });
    },
    async list() {
      const s3 = await cfg.getClient();
      const out: DomainRecord[] = [];
      let token: string | undefined;
      try {
        do {
          const res = await s3.send(
            new ListObjectsV2Command({
              Bucket: cfg.bucket,
              Prefix: cfg.paths.domainRecordDir(),
              ContinuationToken: token,
            }),
          );
          for (const o of res.Contents ?? []) {
            if (!o.Key?.endsWith("/domain.json")) continue;
            const rec = await readRecord(o.Key);
            if (rec) out.push(rec);
          }
          token = res.NextContinuationToken;
        } while (token);
      } catch {
        /* no grant / nothing there → empty list, not an error */
      }
      return out;
    },
    async status(h) {
      return readRecord(cfg.paths.domainRecord(host(h)));
    },
    async emailStatus() {
      return readJson<EmailStatus>(cfg.paths.emailStatus());
    },
    async senders() {
      return readJson<EmailSenders>(cfg.paths.emailSenders());
    },
    async useForAuth(h) {
      await drop(authSenderRequestId(h), authSenderRequest(h));
    },
    async setSenderName(name) {
      await drop(senderNameRequestId(), senderNameRequest(name));
    },
  };
}
